import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Room, RoomEvent, Track, createLocalTracks } from "livekit-client";
import { useContextStateManagement } from "../../context/ContextApi";

const CALL_STATE = {
  IDLE: "idle",
  CONNECTING: "connecting",
  IN_CALL: "in_call",
  ENDED: "ended",
};

const DEFAULT_TRANSCRIPT = [
  {
    speaker: "Agent",
    text: "Transcript preview will bind here when the calling engine exposes it.",
  },
];

const DEFAULT_NOTES = "";
const DEV_MODE = process.env.REACT_APP_DIALER_DEV_MODE === "true";
const DEVELOPMENT_CONNECT_DELAY_MS = 900;
const DEVELOPMENT_CALL_ID = "dev-call-12345";

const DIALER_LOCK_KEY = "cm_dialer_active_call";
const DIALER_LOCK_HEARTBEAT_MS = 4000;
const DIALER_LOCK_STALE_MS = 10000;
const DIALER_LOCK_POLL_MS = 2000;

const makeTabId = () =>
  `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const readDialerLock = () => {
  try {
    const raw = window.localStorage.getItem(DIALER_LOCK_KEY);
    if (!raw) return null;
    const lock = JSON.parse(raw);
    if (!lock?.tabId || !lock?.updatedAt) return null;
    if (Date.now() - lock.updatedAt > DIALER_LOCK_STALE_MS) return null;
    return lock;
  } catch (_) {
    return null;
  }
};

const writeDialerLock = (tabId) => {
  try {
    window.localStorage.setItem(
      DIALER_LOCK_KEY,
      JSON.stringify({ tabId, updatedAt: Date.now() }),
    );
  } catch (_) {}
};

const clearDialerLock = (tabId) => {
  try {
    const lock = readDialerLock();
    if (!lock || lock.tabId === tabId) {
      window.localStorage.removeItem(DIALER_LOCK_KEY);
    }
  } catch (_) {}
};

const relativeFormatter =
  typeof Intl !== "undefined" && Intl.RelativeTimeFormat
    ? new Intl.RelativeTimeFormat("en", { numeric: "auto" })
    : null;

const pickFirstValue = (record, keys) => {
  for (const key of keys) {
    const value = record?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
};

const toCallId = (payload) =>
  pickFirstValue(payload, [
    "dialerSessionId",
    "DialerSessionId",
    "callId",
    "CallId",
    "rootCallSid",
    "RootCallSid",
    "callSid",
    "CallSid",
    "sid",
    "Sid",
    "legSid",
    "LegSid",
    "roomName",
    "RoomName",
    "id",
    "Id",
  ]);

const toDurationSeconds = (record) => {
  const directValue = Number(
    pickFirstValue(record, [
      "billableSeconds",
      "BillableSeconds",
      "Duration",
      "duration",
      "durationSec",
      "DurationSec",
      "totalSeconds",
      "TotalSeconds",
    ]),
  );

  if (Number.isFinite(directValue)) {
    return directValue;
  }

  const legs = Array.isArray(record?.legs) ? record.legs : [];

  return legs.reduce((total, leg) => {
    const seconds = Number(
      pickFirstValue(leg, ["durationSec", "DurationSec", "duration", "Duration"]),
    );

    return total + (Number.isFinite(seconds) ? seconds : 0);
  }, 0);
};

const toOutcome = (record) => {
  const rawStatus = `${pickFirstValue(record, [
    "Outcome",
    "outcome",
    "Status",
    "status",
    "CallStatus",
    "callStatus",
    "Disposition",
    "disposition",
  ])}`.toLowerCase();

  if (rawStatus.includes("voicemail")) {
    return "Voicemail";
  }

  if (
    rawStatus.includes("miss") ||
    rawStatus.includes("no answer") ||
    rawStatus.includes("no-answer") ||
    rawStatus.includes("busy") ||
    rawStatus.includes("failed") ||
    rawStatus.includes("cancel")
  ) {
    return "Missed";
  }

  return toDurationSeconds(record) > 0 ? "Completed" : "Missed";
};

const formatRelativeTime = (value) => {
  if (!value) {
    return "Unknown";
  }

  const timestamp = new Date(value).getTime();

  if (Number.isNaN(timestamp)) {
    return `${value}`;
  }

  const seconds = Math.round((timestamp - Date.now()) / 1000);
  const absoluteSeconds = Math.abs(seconds);

  if (relativeFormatter) {
    if (absoluteSeconds < 60) {
      return relativeFormatter.format(seconds, "second");
    }

    if (absoluteSeconds < 3600) {
      return relativeFormatter.format(Math.round(seconds / 60), "minute");
    }

    if (absoluteSeconds < 86400) {
      return relativeFormatter.format(Math.round(seconds / 3600), "hour");
    }

    return relativeFormatter.format(Math.round(seconds / 86400), "day");
  }

  if (absoluteSeconds < 60) {
    return "Just now";
  }

  if (absoluteSeconds < 3600) {
    return `${Math.round(absoluteSeconds / 60)} min ago`;
  }

  if (absoluteSeconds < 86400) {
    return `${Math.round(absoluteSeconds / 3600)} hr ago`;
  }

  return `${Math.round(absoluteSeconds / 86400)} day ago`;
};

const toRecentCallItem = (record) => {
  const number = pickFirstValue(record, [
    "To",
    "to",
    "phoneNumber",
    "PhoneNumber",
    "number",
    "Number",
    "ContactNumber",
  ]);

  return {
    id: toCallId(record) || `${number}-${pickFirstValue(record, ["DisplayStartedAt", "StartedAt", "startedAt"])}`,
    callId: toCallId(record),
    number: `${number || "Unknown number"}`,
    timeLabel: formatRelativeTime(
      pickFirstValue(record, [
        "DisplayStartedAt",
        "displayStartedAt",
        "StartedAt",
        "startedAt",
        "CallDate",
        "callDate",
      ]),
    ),
    outcome: toOutcome(record),
    note: `${pickFirstValue(record, ["Notes", "notes", "Note", "note", "CallNotes", "callNotes"])}`,
    savedName: `${pickFirstValue(record, [
      "ContactName",
      "contactName",
      "SavedName",
      "savedName",
      "Name",
      "name",
    ])}`,
    raw: record,
  };
};

const toRequestError = (response, fallbackMessage) => {
  if (!response) {
    return fallbackMessage;
  }

  if (typeof response === "string") {
    return /error|failed|invalid|expired|unable|missing|not found/i.test(response)
      ? response
      : "";
  }

  if (response?.error) {
    return response.error;
  }

  return "";
};

const createDevelopmentCallPayload = ({ dialValue, selectedAgent, practiceId }) => {
  return {
    roomName: "dev-room-12345",
    callId: DEVELOPMENT_CALL_ID,
    livekitToken: "development-mock-token",
    livekitWsUrl: "wss://development-mock-livekit.invalid",
    to: dialValue.trim(),
    from: selectedAgent?.phoneNumber || "",
    practiceId,
  };
};

const mergeRecentCallLists = (developmentItems, currentItems) => {
  const developmentIds = new Set(developmentItems.map((item) => item.id));
  const nonDevelopmentItems = currentItems.filter(
    (item) => !developmentIds.has(item.id),
  );

  return [...developmentItems, ...nonDevelopmentItems];
};

export function useCallEngine({ dialValue = "" } = {}) {
  const { PracticeId, token, fetchUserRecord, usersPostRequest, dateRangeRequest } =
    useContextStateManagement();

  const tabIdRef = useRef(makeTabId());

  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState("");

  const [recentCalls, setRecentCalls] = useState([]);
  const [loadingRecentCalls, setLoadingRecentCalls] = useState(false);

  const [callState, setCallState] = useState(CALL_STATE.IDLE);
  const [callError, setCallError] = useState("");
  const [isCallLockedByOtherTab, setIsCallLockedByOtherTab] = useState(() => {
    const lock = readDialerLock();
    return Boolean(lock) && lock.tabId !== tabIdRef.current;
  });
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [sipJoined, setSipJoined] = useState(false);
  const [currentCallId, setCurrentCallId] = useState("");
  const [currentDialerSessionId, setCurrentDialerSessionId] = useState(null);

  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesFeedback, setNotesFeedback] = useState("");
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [contactFeedback, setContactFeedback] = useState("");

  const roomRef = useRef(null);
  const localTracksRef = useRef([]);
  const audioElementsRef = useRef([]);
  const roomNameRef = useRef("");
  const timerRef = useRef(null);
  const historySyncTimeoutRef = useRef(null);
  const mockConnectTimeoutRef = useRef(null);
  const hangupCalledRef = useRef(false);
  const handleHangupRef = useRef(null);
  const selectedAgentRef = useRef(null);
  const devRecentCallsRef = useRef([]);
  const devCallDraftRef = useRef(null);

  useEffect(() => {
    const tabId = tabIdRef.current;

    const syncLockState = () => {
      const lock = readDialerLock();
      setIsCallLockedByOtherTab(Boolean(lock) && lock.tabId !== tabId);
    };

    syncLockState();

    const handleStorage = (event) => {
      if (event.key === DIALER_LOCK_KEY || event.key === null) {
        syncLockState();
      }
    };

    window.addEventListener("storage", handleStorage);
    const pollTimer = window.setInterval(syncLockState, DIALER_LOCK_POLL_MS);

    const handleUnload = () => clearDialerLock(tabId);
    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("pagehide", handleUnload);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("pagehide", handleUnload);
      window.clearInterval(pollTimer);
      clearDialerLock(tabId);
    };
  }, []);

  useEffect(() => {
    const tabId = tabIdRef.current;
    const isOngoing =
      callState === CALL_STATE.CONNECTING || callState === CALL_STATE.IN_CALL;

    if (!isOngoing) {
      return undefined;
    }

    writeDialerLock(tabId);
    const heartbeatTimer = window.setInterval(
      () => writeDialerLock(tabId),
      DIALER_LOCK_HEARTBEAT_MS,
    );

    return () => {
      window.clearInterval(heartbeatTimer);
      clearDialerLock(tabId);
    };
  }, [callState]);

  const selectedAgent = useMemo(
    () => agents.find((agent) => agent.id === selectedAgentId) || null,
    [agents, selectedAgentId],
  );

  useEffect(() => {
    selectedAgentRef.current = selectedAgent;
  }, [selectedAgent]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearMockTimers = useCallback(() => {
    if (mockConnectTimeoutRef.current) {
      window.clearTimeout(mockConnectTimeoutRef.current);
      mockConnectTimeoutRef.current = null;
    }
  }, []);

  const upsertDevelopmentRecentCall = useCallback((nextItem) => {
    const nextDevelopmentItems = [
      nextItem,
      ...devRecentCallsRef.current.filter((item) => item.id !== nextItem.id),
    ];

    devRecentCallsRef.current = nextDevelopmentItems;
    setRecentCalls((currentItems) =>
      mergeRecentCallLists(nextDevelopmentItems, currentItems),
    );
  }, []);

  const updateDevelopmentRecentCall = useCallback((entryId, updates) => {
    if (!entryId) {
      return;
    }

    const nextDevelopmentItems = devRecentCallsRef.current.map((item) =>
      item.id === entryId ? { ...item, ...updates } : item,
    );

    devRecentCallsRef.current = nextDevelopmentItems;
    setRecentCalls((currentItems) =>
      mergeRecentCallLists(nextDevelopmentItems, currentItems),
    );
  }, []);

  const cleanupMedia = useCallback(() => {
    if (roomRef.current) {
      try {
        roomRef.current.disconnect();
      } catch (_) {}
      roomRef.current = null;
    }

    for (const track of localTracksRef.current) {
      try {
        track.stop();
      } catch (_) {}
    }

    localTracksRef.current = [];

    for (const element of audioElementsRef.current) {
      try {
        element.pause();
        element.remove();
      } catch (_) {}
    }

    audioElementsRef.current = [];
  }, []);

  const cleanupFailedStart = useCallback(() => {
    stopTimer();

    for (const track of localTracksRef.current) {
      try {
        track.stop();
      } catch (_) {}
    }

    localTracksRef.current = [];

    for (const element of audioElementsRef.current) {
      try {
        element.pause();
        element.remove();
      } catch (_) {}
    }

    audioElementsRef.current = [];
    roomRef.current = null;
  }, [stopTimer]);

  const fetchAgents = useCallback(async () => {
    if (!PracticeId || !token) {
      return;
    }

    setLoadingAgents(true);

    try {
      const data = await fetchUserRecord(
        token,
        `/api/getAssignedPhoneNumbers?practiceId=${PracticeId}`,
      );

      if (Array.isArray(data)) {
        const inboundAgents = data.filter(
          (item) =>
            item.assignmentType === "Practice Inbound" ||
            item.assignmentType === "Practice Outbound" ||
            item.assignmentType === "Agent",
        );

        const mappedAgents = {};

        inboundAgents.forEach((item) => {
          const key = item.phoneNumber;

          if (!mappedAgents[key]) {
            mappedAgents[key] = {
              id: key,
              phoneNumber: item.phoneNumber,
              friendlyName: item.friendlyName || item.phoneNumber,
              agentName:
                item.providerName || item.friendlyName || item.phoneNumber,
              sid: item.sid || null,
            };

            return;
          }

          if (item.providerName) {
            mappedAgents[key].agentName = item.providerName;
          }
        });

        const nextAgents = Object.values(mappedAgents);
        setAgents(nextAgents);
        setSelectedAgentId((currentId) =>
          currentId && nextAgents.some((agent) => agent.id === currentId)
            ? currentId
            : nextAgents[0]?.id || "",
        );
      } else {
        setAgents([]);
        setSelectedAgentId("");
      }
    } finally {
      setLoadingAgents(false);
    }
  }, [PracticeId, token, fetchUserRecord]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const fetchRecentCalls = useCallback(
    async (agent = selectedAgentRef.current) => {
      if (!agent || !token) {
        setRecentCalls([]);
        return;
      }

      setLoadingRecentCalls(true);

      try {
        const data = await dateRangeRequest(token, "/api/dialerCallHistory", {
          agentNumber: agent.phoneNumber,
          practiceId: PracticeId,
        });

        const backendItems = Array.isArray(data) ? data.map(toRecentCallItem) : [];
        setRecentCalls(
          DEV_MODE
            ? mergeRecentCallLists(devRecentCallsRef.current, backendItems)
            : backendItems,
        );
      } finally {
        setLoadingRecentCalls(false);
      }
    },
    [PracticeId, dateRangeRequest, token],
  );

  useEffect(() => {
    if (selectedAgent) {
      fetchRecentCalls(selectedAgent);
    }
  }, [fetchRecentCalls, selectedAgent]);

  const scheduleRecentCallsRefresh = useCallback(
    (delayMs = 8000) => {
      if (historySyncTimeoutRef.current) {
        window.clearTimeout(historySyncTimeoutRef.current);
      }

      historySyncTimeoutRef.current = window.setTimeout(() => {
        fetchRecentCalls(selectedAgentRef.current);
      }, delayMs);
    },
    [fetchRecentCalls],
  );

  const startConnectedTimer = useCallback(() => {
    setSipJoined(true);

    if (!timerRef.current) {
      setCallDuration(0);
      timerRef.current = window.setInterval(() => {
        setCallDuration((currentDuration) => currentDuration + 1);
      }, 1000);
    }
  }, []);

  const startCall = useCallback(async () => {
    if (!dialValue.trim() || !selectedAgent || callState !== CALL_STATE.IDLE) {
      return false;
    }

    const existingLock = readDialerLock();
    if (existingLock && existingLock.tabId !== tabIdRef.current) {
      setIsCallLockedByOtherTab(true);
      setCallError("Call already in progress in another tab");
      return false;
    }

    setCallState(CALL_STATE.CONNECTING);
    setCallError("");
    setNotesFeedback("");
    setContactFeedback("");
    setSipJoined(false);
    setCallDuration(0);
    setIsMuted(false);
    setIsOnHold(false);
    setCurrentCallId("");
    setCurrentDialerSessionId(null);
    devCallDraftRef.current = null;
    hangupCalledRef.current = false;

    try {
      const response = DEV_MODE
        ? createDevelopmentCallPayload({
            dialValue,
            selectedAgent,
            practiceId: PracticeId,
          })
        : await usersPostRequest(token, "/api/dialerCall", {
            to: dialValue.trim(),
            from: selectedAgent.phoneNumber,
            practiceId: PracticeId,
          });

      if (!response?.livekitToken || !response?.livekitWsUrl || !response?.roomName) {
        const message =
          typeof response === "string"
            ? response
            : response?.error || "Invalid response from server";

        throw new Error(message);
      }

      roomNameRef.current = response.roomName;
      setCurrentDialerSessionId(response.dialerSessionId || null);
      setCurrentCallId(
        response.dialerSessionId || toCallId(response) || response.roomName,
      );

      if (DEV_MODE) {
        devCallDraftRef.current = {
          entryId: `${DEVELOPMENT_CALL_ID}-${Date.now()}`,
          callId: DEVELOPMENT_CALL_ID,
          roomName: response.roomName,
          number: dialValue.trim(),
          outboundNumber: selectedAgent.phoneNumber,
          notes: "",
          savedName: "",
          startedAt: new Date().toISOString(),
        };

        await new Promise((resolve) => {
          mockConnectTimeoutRef.current = window.setTimeout(() => {
            mockConnectTimeoutRef.current = null;
            resolve();
          }, DEVELOPMENT_CONNECT_DELAY_MS);
        });

        setCallState(CALL_STATE.IN_CALL);
        startConnectedTimer();
        return true;
      }

      const localTracks = await createLocalTracks({ audio: true, video: false });
      localTracksRef.current = localTracks;

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });

      roomRef.current = room;

      const markAnswered = () => {
        startConnectedTimer();
      };

      room.on(RoomEvent.ParticipantAttributesChanged, (_changed, participant) => {
        const status = participant.attributes?.["sip.callStatus"];

        if (status === "active") {
          markAnswered();
        }
      });

      room.on(RoomEvent.ParticipantConnected, (participant) => {
        if (participant.attributes?.["sip.callStatus"] === "active") {
          markAnswered();
        }
      });

      room.on(RoomEvent.ParticipantDisconnected, () => {
        if (room.remoteParticipants.size === 0) {
          setSipJoined(false);
          handleHangupRef.current?.(true);
        }
      });

      room.on(RoomEvent.Disconnected, () => {
        handleHangupRef.current?.(true);
      });

      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === Track.Kind.Audio) {
          const audioElement = track.attach();
          audioElement.autoplay = true;
          document.body.appendChild(audioElement);
          audioElementsRef.current.push(audioElement);
          audioElement.play().catch(() => {});
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        if (track.kind === Track.Kind.Audio) {
          track.detach().forEach((element) => {
            element.pause();
            element.remove();
          });
        }
      });

      await room.connect(response.livekitWsUrl, response.livekitToken);

      for (const track of localTracks) {
        await room.localParticipant.publishTrack(track);
      }

      setCallState(CALL_STATE.IN_CALL);
      setCallDuration(0);

      const alreadyActive = [...room.remoteParticipants.values()].some(
        (participant) => participant.attributes?.["sip.callStatus"] === "active",
      );

      if (alreadyActive) {
        markAnswered();
      }

      return true;
    } catch (error) {
      setCallError(error?.message || "Failed to connect. Please try again.");
      setCallState(CALL_STATE.IDLE);
      clearMockTimers();
      cleanupFailedStart();
      roomNameRef.current = "";
      setCurrentCallId("");
      setCurrentDialerSessionId(null);
      devCallDraftRef.current = null;
      return false;
    }
  }, [
    PracticeId,
    callState,
    cleanupFailedStart,
    clearMockTimers,
    dialValue,
    selectedAgent,
    startConnectedTimer,
    token,
    usersPostRequest,
  ]);

  const endCall = useCallback(
    async (remoteInitiated = false) => {
      if (hangupCalledRef.current) {
        return;
      }

      hangupCalledRef.current = true;
      clearMockTimers();
      stopTimer();
      setSipJoined(false);
      setIsMuted(false);
      setIsOnHold(false);

      if (DEV_MODE) {
        const developmentDraft = devCallDraftRef.current;

        if (developmentDraft) {
          upsertDevelopmentRecentCall({
            id: developmentDraft.entryId,
            callId: developmentDraft.callId,
            number: developmentDraft.number,
            timeLabel: formatRelativeTime(new Date().toISOString()),
            outcome: "Completed",
            note: developmentDraft.notes,
            savedName: developmentDraft.savedName,
            raw: {
              To: developmentDraft.number,
              From: developmentDraft.outboundNumber,
              DisplayStartedAt: developmentDraft.startedAt,
            },
          });
        }

        roomNameRef.current = "";
        setCallState(CALL_STATE.ENDED);

        if (!remoteInitiated) {
          setCallError("");
        }

        return;
      }

      cleanupMedia();

      if (roomNameRef.current) {
        try {
          await usersPostRequest(token, "/api/dialerHangup", {
            dialerSessionId: currentDialerSessionId,
            roomName: roomNameRef.current,
            practiceId: PracticeId,
          });
        } catch (_) {}

        roomNameRef.current = "";
      }

      setCallState(CALL_STATE.ENDED);

      if (!remoteInitiated) {
        setCallError("");
      }

      scheduleRecentCallsRefresh();
    },
    [
      PracticeId,
      cleanupMedia,
      clearMockTimers,
      currentDialerSessionId,
      scheduleRecentCallsRefresh,
      stopTimer,
      token,
      usersPostRequest,
    ],
  );

  handleHangupRef.current = endCall;

  useEffect(() => {
    return () => {
      stopTimer();
      clearMockTimers();

      if (historySyncTimeoutRef.current) {
        window.clearTimeout(historySyncTimeoutRef.current);
      }

      cleanupMedia();
    };
  }, [cleanupMedia, clearMockTimers, stopTimer]);

  const toggleMute = useCallback(async () => {
    if (DEV_MODE) {
      if (callState !== CALL_STATE.IN_CALL) {
        return;
      }

      setIsMuted((currentValue) => !currentValue);
      return;
    }

    if (!roomRef.current) {
      return;
    }

    const micTrack = roomRef.current.localParticipant
      .getTrackPublications()
      .find((publication) => publication.track?.kind === Track.Kind.Audio);

    if (!micTrack) {
      return;
    }

    if (isMuted) {
      await roomRef.current.localParticipant.setMicrophoneEnabled(true);
    } else {
      await roomRef.current.localParticipant.setMicrophoneEnabled(false);
    }

    setIsMuted((currentValue) => !currentValue);
  }, [callState, isMuted]);

  const toggleHold = useCallback(async () => {
    if (DEV_MODE) {
      if (callState !== CALL_STATE.IN_CALL) {
        return false;
      }

      setIsOnHold((currentValue) => !currentValue);
      return true;
    }

    // The legacy dialer did not include hold support.
    // This hook keeps the button wired, but does not invent new telephony logic.
    return false;
  }, [callState]);

  const saveNotes = useCallback(
    async (notes) => {
      const callId = currentCallId || roomNameRef.current;

      if (!currentDialerSessionId && !callId) {
        setNotesFeedback("A call id is not available yet for notes.");
        return false;
      }

      setIsSavingNotes(true);
      setNotesFeedback("");

      try {
        if (DEV_MODE) {
          if (devCallDraftRef.current) {
            devCallDraftRef.current = {
              ...devCallDraftRef.current,
              notes,
            };
          }

          updateDevelopmentRecentCall(devCallDraftRef.current?.entryId, {
            note: notes,
          });
          setNotesFeedback("Notes saved.");
          return true;
        }

        const response = await usersPostRequest(token, "/api/dialerSaveNotes", {
          callId,
          dialerSessionId: currentDialerSessionId,
          notes,
          practiceId: PracticeId,
        });

        const requestError = toRequestError(
          response,
          "Unable to save notes right now.",
        );

        if (requestError) {
          throw new Error(requestError);
        }

        setNotesFeedback("Notes saved.");
        await fetchRecentCalls(selectedAgentRef.current);
        return true;
      } catch (error) {
        setNotesFeedback(error?.message || "Unable to save notes right now.");
        return false;
      } finally {
        setIsSavingNotes(false);
      }
    },
    [
      PracticeId,
      currentCallId,
      currentDialerSessionId,
      fetchRecentCalls,
      token,
      updateDevelopmentRecentCall,
      usersPostRequest,
    ],
  );

  const saveContact = useCallback(
    async ({ number, name }) => {
      if (!number?.trim() || !name?.trim()) {
        setContactFeedback("Enter a contact name before saving.");
        return false;
      }

      setIsSavingContact(true);
      setContactFeedback("");

      try {
        if (DEV_MODE) {
          if (devCallDraftRef.current) {
            devCallDraftRef.current = {
              ...devCallDraftRef.current,
              savedName: name.trim(),
            };
          }

          updateDevelopmentRecentCall(devCallDraftRef.current?.entryId, {
            savedName: name.trim(),
          });
          setContactFeedback("Contact saved.");
          return true;
        }

        const response = await usersPostRequest(token, "/api/saveContact", {
          number: number.trim(),
          name: name.trim(),
          practiceId: PracticeId,
        });

        const requestError = toRequestError(
          response,
          "Unable to save the contact right now.",
        );

        if (requestError) {
          throw new Error(requestError);
        }

        setContactFeedback("Contact saved.");
        await fetchRecentCalls(selectedAgentRef.current);
        return true;
      } catch (error) {
        setContactFeedback(
          error?.message || "Unable to save the contact right now.",
        );
        return false;
      } finally {
        setIsSavingContact(false);
      }
    },
    [PracticeId, fetchRecentCalls, token, updateDevelopmentRecentCall, usersPostRequest],
  );

  const resetDialer = useCallback(() => {
    if (historySyncTimeoutRef.current) {
      window.clearTimeout(historySyncTimeoutRef.current);
      historySyncTimeoutRef.current = null;
    }

    clearMockTimers();
    stopTimer();
    setCallState(CALL_STATE.IDLE);
    setCallError("");
    setCallDuration(0);
    setSipJoined(false);
    setIsMuted(false);
    setIsOnHold(false);
    setCurrentCallId("");
    setCurrentDialerSessionId(null);
    setNotesFeedback("");
    setContactFeedback("");
    devCallDraftRef.current = null;
    hangupCalledRef.current = false;
  }, [clearMockTimers, stopTimer]);

  return {
    callState,
    isCallLockedByOtherTab,
    callPhase:
      callState === CALL_STATE.IN_CALL
        ? "active"
        : callState === CALL_STATE.ENDED
          ? "ended"
          : "ready",
    dialValue,
    outboundNumber: selectedAgent?.phoneNumber || "",
    selectedAgent,
    selectedAgentId,
    setSelectedAgentId,
    agents,
    loadingAgents,
    recentCalls,
    loadingRecentCalls,
    refreshRecentCalls: fetchRecentCalls,
    callError,
    currentCallId,
    defaultNotes: DEFAULT_NOTES,
    transcript: DEFAULT_TRANSCRIPT,
    callDuration,
    duration: callDuration,
    isMuted,
    isOnHold,
    canHold: DEV_MODE,
    DEV_MODE,
    isConnected: sipJoined,
    startCall,
    endCall,
    toggleMute,
    toggleHold,
    saveNotes,
    isSavingNotes,
    notesFeedback,
    saveContact,
    isSavingContact,
    contactFeedback,
    resetDialer,
  };
}

export default useCallEngine;

